import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Package, Clock, Landmark, Mail, MessageCircle, CalendarPlus, Sparkles } from "lucide-react";
import { OpenInAdmin } from "@/components/OpenInAdmin";
import { WaBusinessLink, useBusinessContact } from "@/lib/whatsapp";

const BANK_FALLBACK = {
  account_name: "Romika C. Browne",
  accounts: [
    { bank: "Clarien Bank", number: "7010042141" },
    { bank: "HSBC", number: "006-031124-011" },
  ],
};

export default function OrderPlaced() {
  const contact = useBusinessContact();
  const BANK = { name: (contact.bank || BANK_FALLBACK).account_name, accounts: (contact.bank || BANK_FALLBACK).accounts };
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const token = params.get("token");
  const { clear } = useCart();
  const { user, checkAuth } = useAuth();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading");
  const cleared = useRef(false);
  const [nickname, setNickname] = useState("");
  const [address, setAddress] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    if (user) {
      setNickname((n) => n || user.nickname || "");
      setAddress((a) => a || user.default_address || "");
    }
  }, [user]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", { nickname: nickname.trim() || null, default_address: address.trim() || null });
      await checkAuth();
      setProfileSaved(true);
      toast.success("Saved — checkout will be faster next time!");
    } catch { toast.error("Could not save details"); }
    finally { setSavingProfile(false); }
  };

  useEffect(() => {
    if (!cleared.current) { clear(); cleared.current = true; }
  }, [clear]);

  useEffect(() => {
    if (!orderId) { setState("error"); return; }
    api.get(`/orders/${orderId}`, { params: token ? { token } : {} })
      .then(({ data }) => { setOrder(data); setState("ok"); })
      .catch(() => setState("error"));
  }, [orderId, token]);

  if (state === "loading")
    return <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="orderplaced-loading"><Loader2 className="animate-spin mx-auto text-[#ff6a13]" size={40} /></div>;

  if (state === "error" || !order)
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="orderplaced-error">
        <h1 className="font-display text-2xl font-bold text-white">Order not found</h1>
        <Link to="/catalog"><Button className="mt-6 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full">Back to Catalog</Button></Link>
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="order-placed">
      <div className="text-center">
        <CheckCircle2 className="mx-auto text-[#10b981]" size={52} />
        <h1 className="font-display text-3xl font-extrabold text-white mt-4">Order placed!</h1>
        <p className="text-zinc-400 mt-2">Complete your order by direct bank deposit using the details below.</p>
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#22c3d6] bg-[#22c3d6]/10 border border-[#22c3d6]/30 rounded-full px-4 py-2 font-mono-p" data-testid="turnaround-badge">
          <Clock size={14} /> Production begins once your deposit is received &amp; your fulfillment date is confirmed
        </div>
      </div>

      <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-6 mt-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500 font-mono-p uppercase">Payment Reference (Order #)</p>
            <p className="font-display text-2xl font-bold text-white font-mono-p" data-testid="order-number">{order.order_number}</p>
          </div>
          <div className="flex items-center gap-3"><OpenInAdmin orderNumber={order.order_number} size="sm" /><Package className="text-[#ff6a13]" size={26} /></div>
        </div>
        <p className="text-xs text-[#ffd600] mt-2">Use this order number as your payee reference so we can match your payment.</p>
      </div>

      <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-6 mt-4">
        {order.requires_deposit ? (
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-zinc-400">Order total</span><span className="font-mono-p text-zinc-300">${order.total.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-300 font-semibold">Deposit to pay now (50%)</span><span className="font-bold font-mono-p text-[#10b981]" data-testid="amount-due">${(order.deposit_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-400">Balance on delivery</span><span className="font-mono-p text-zinc-300">${(order.balance_due || 0).toFixed(2)}</span></div>
            <p className="text-xs text-zinc-500 pt-1">Bulk orders over 10 items require a 50% deposit; the balance and delivery date are agreed after your order is placed.</p>
          </div>
        ) : (
          <div className="flex justify-between items-center"><span className="text-zinc-300 font-semibold">Amount to deposit</span><span className="font-display text-2xl font-black text-white font-mono-p" data-testid="amount-due">${order.total.toFixed(2)}</span></div>
        )}
      </div>

      <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-6 mt-4" data-testid="bank-details">
        <div className="flex items-center gap-2 text-white"><Landmark size={18} className="text-[#ff6a13]" /><h2 className="font-display text-lg font-bold">Direct Deposit Details</h2></div>
        <div className="mt-4 space-y-3">
          <div className="flex justify-between"><span className="text-zinc-500 text-sm">Account name</span><span className="text-white font-semibold">{BANK.name}</span></div>
          {BANK.accounts.map((a) => (
            <div key={a.bank} className="flex justify-between border-t border-zinc-800 pt-3">
              <span className="text-zinc-500 text-sm">{a.bank}</span>
              <span className="text-white font-mono-p">{a.number}</span>
            </div>
          ))}
          {contact.bank?.qr_url && (
            <div className="border-t border-zinc-800 pt-4 flex items-center gap-4" data-testid="pay-qr">
              <div className="bg-white rounded-xl p-2 shrink-0"><img src={contact.bank.qr_url} alt="Scan to pay" className="w-32 h-32 object-contain" /></div>
              <div><p className="text-sm font-semibold text-white">Scan to pay</p><p className="text-xs text-zinc-400 mt-1">Open your banking app, scan this code and use <span className="font-mono-p text-[#ffd600]">{order.order_number}</span> as the reference.</p></div>
            </div>
          )}
        </div>
        <div className="mt-4 rounded-xl border border-[#ff6a13]/40 bg-[#ff6a13]/10 p-4" data-testid="proof-of-payment">
          <p className="text-sm font-semibold text-white">📸 Send proof of payment</p>
          <p className="text-xs text-zinc-300 mt-1 leading-relaxed">After you deposit, send your receipt / screenshot so we can confirm and start production faster. Include your order number <span className="font-mono-p text-[#ffd600]">{order.order_number}</span>.</p>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <a href={`mailto:${contact.business?.email || "allwaysgraphic@gmail.com"}?subject=Proof of Payment — Order ${order.order_number}`} data-testid="proof-email-link"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1e222d] border border-zinc-700 hover:border-[#ff6a13] rounded-full px-4 py-2 text-sm text-white">
              <Mail size={15} /> Email receipt
            </a>
            <WaBusinessLink num={contact.whatsapp_number} text={`Hi All-Ways Graphic, here is my proof of payment for order ${order.order_number}.`} data-testid="proof-whatsapp-link"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366]/15 border border-[#25D366]/50 hover:border-[#25D366] rounded-full px-4 py-2 text-sm text-white">
              <MessageCircle size={15} /> WhatsApp {contact.whatsapp_display}
            </WaBusinessLink>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-3 leading-relaxed">We'll confirm your deposit and begin production once received.</p>
      </div>

      {user && !profileSaved && (
        <div className="bg-[#12141a] border border-[#22c3d6]/30 rounded-2xl p-6 mt-4" data-testid="save-details-card">
          <div className="flex items-center gap-2 text-white"><Sparkles size={18} className="text-[#22c3d6]" /><h2 className="font-display text-lg font-bold">Make your next order 1-click</h2></div>
          <p className="text-sm text-zinc-400 mt-1.5">Save your delivery address and a nickname now — we'll pre-fill everything next time you check out.</p>
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-zinc-300">Nickname <span className="text-zinc-500 font-normal">(optional)</span></Label>
              <Input data-testid="profile-nickname-input" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="e.g. Sam from the studio" className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#22c3d6] focus:ring-[#22c3d6]" />
            </div>
            <div>
              <Label className="text-zinc-300">Delivery address</Label>
              <Textarea data-testid="profile-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, city, parish, postal code" className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#22c3d6] focus:ring-[#22c3d6]" />
            </div>
            <div className="flex gap-3">
              <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn" className="bg-[#22c3d6] hover:bg-[#1aa9ba] text-black rounded-full font-semibold disabled:opacity-40">{savingProfile ? "Saving…" : "Save for next time"}</Button>
              <Button variant="outline" onClick={() => setProfileSaved(true)} data-testid="skip-profile-btn" className="rounded-full border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 font-semibold">Not now</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 justify-center mt-8">
        <Link to="/account"><Button className="bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full font-semibold">My Orders</Button></Link>
        <Link to="/track"><Button variant="outline" className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-900">Track Order</Button></Link>
        {order.fulfillment_date && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/calendar.ics?token=${token || order.access_token || ""}`} data-testid="ics-download-link"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-transparent text-white hover:bg-zinc-900 px-5 h-10 text-sm font-semibold">
            <CalendarPlus size={16} /> Add to Calendar
          </a>
        )}
      </div>
    </div>
  );
}
